import {
  Box,
  Typography,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
  Stack,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import Loader from "Components/Loader";
import ImgPath from "Constants/Imgs";
import { CaseListType } from "DataTypes/Case.type";
import { UserInitialStateType } from "DataTypes/Redux";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CaseApi from "Service/Case.api";
import { disconnectChatHub } from "Service/signalRService";
import DateTime from "Utils/DateTime";
import { FormateCaseList } from "./Formatter";
import {
  filterByAnyValue,
  FormatDropdownData,
  HandleFormChange,
  formatUserType,
} from "Utils/Common";
import { useParams } from "react-router-dom";
import { GlobalResType, HandleChangeProps } from "DataTypes/Common";
import { DropdownArrPropsType, FormDataType } from "DataTypes/Form";
import ModalLayout from "Layouts/Modal";
import { CustomModal } from "Components/Modal";
import { AddNewCaseForm } from "./Data";

const ChatListComponent = (props: any) => {
  const { setShowChatList, getSelectedCase, onListLoaded } = props;
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const { orderType } = useParams<{ orderType: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [caseList, setCaseList] = useState<CaseListType[]>([]);
  const [orderDropdown, setOrderDropdown] = useState<DropdownArrPropsType[]>(
    []
  );
  const [caseDropdown, setCaseDropdown] = useState<DropdownArrPropsType[]>([]);
  const [caseListCopy, setCaseListCopy] = useState<CaseListType[]>([]);
  const [createCaseFormData, setCreateCaseFormData] = useState<FormDataType[]>(
    []
  );
  const [selectedCase, setSelectedCase] = useState<null | CaseListType>(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [inputVal, setInputVal] = useState<string>("");
  const [isLegalCounselChat, setIsLegalCounselChat] = useState<boolean>(false);

  useEffect(() => {
    handleOnload();
  }, [orderType]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      getChatListByUserId();
    }, 500); // Debounce search for 500ms

    return () => clearTimeout(delayDebounceFn);
  }, [inputVal]);

  useEffect(() => {
    return () => {
      // cleanup on chat screen exit
      // disconnectChatHub();
    };
  }, []);

  const handleOnload = async () => {
    setLoading(true);
    setIsLegalCounselChat(false);
    const caseRes: GlobalResType = await CaseApi.fetchCaseTypes();
    let caseTypeDropList: DropdownArrPropsType[] = [];
    let orderTypeDropList: DropdownArrPropsType[] = [];
    if (caseRes?.success) {
      caseTypeDropList = FormatDropdownData(caseRes?.data, "id", "name", "id");
      setCaseDropdown(caseTypeDropList);
    }

    const OrderTypeRes: GlobalResType = await CaseApi.fetchOrderTypes();

    if (OrderTypeRes?.success) {
      orderTypeDropList = FormatDropdownData(
        OrderTypeRes?.data,
        "id",
        "name",
        "id"
      );
      setOrderDropdown(orderTypeDropList);
    }

    // 1. Define your allowed legal IDs
    const targetIds = [43, 44, 51];
    // Check if the current order is in our list
    const isTargetOrder = targetIds.includes(Number(orderType));

    // Assign the object only if it's a target order
    const legalObject = isTargetOrder
      ? orderTypeDropList?.find(el => Number(el.id) === Number(orderType))
      : null;
    // console.log(legalObject, "legalObject");
    if (legalObject) {
      if (legalObject.value == orderType) {
        setIsLegalCounselChat(true);
      }
    }
    await getChatListByUserId();
    setLoading(false);
  };

  const getChatListByUserId: () => void = async () => {
    // If orderType is "admin-support", pass 53 (Admin Support ID), else pass orderType
    const isAdminSupportRoute = orderType === "admin-support";
    const orderTypeIdToPass = isAdminSupportRoute ? 53 : (orderType ?? null);

    setLoading(true);
    const res = await CaseApi.getCaseListByUserId({
      clientId: myInfo.id,
      orderTypeId: orderTypeIdToPass,
      searchQuery: inputVal, // Pass the search query to the backend
      pageNumber: 1,
      pageSize: 100,
    });

    if (res?.success) {
      // res.data is now a PaginatedListDto, so we need res.data.data
      let formattedRes = FormateCaseList(res?.data?.data);

      if (isAdminSupportRoute) {
        // Only show "Admin support" cases
        formattedRes = formattedRes.filter((c: CaseListType) =>
          c.orderType?.trim().toLowerCase() === "admin support"
        );
      } else {
        if (!orderType) {
          formattedRes = formattedRes.filter((c: CaseListType) =>
            c.orderType?.trim().toLowerCase() !== "admin support"
          );
        }
      }
      setCaseList(formattedRes);
      setCaseListCopy(formattedRes);
      // This sends the data back to the Parent
      if (onListLoaded) onListLoaded(formattedRes);
    }
    setLoading(false);
  };

  const getInitials: (orderType: string) => string = (orderType) => {
    if (!orderType) return "";
    const words = orderType.split(" ");
    let initials = "";
    for (let i = 0; i < words.length && i < 2; i++) {
      initials += words[i].charAt(0).toUpperCase();
    }
    return initials;
  };

  const handleCreateCase = async () => {

    // Find the case type based on the order type and create the case hardcoded
    // 1. Define your allowed legal IDs
    const targetIds = [43, 44, 51];
    // 2. Find the specific object where the ID matches the current orderType 
    // AND ensure that ID is within your targetIds list
    const findCase = orderDropdown?.find((el) =>
      Number(el.id) === Number(orderType) && targetIds.includes(Number(el.id))
    );
    const payload = {
      clientId: myInfo.id,
      caseTypeId: findCase?.value ?? null,
      orderTypeId: orderType,
      description: createCaseFormData[0].value,
    };
    const res = await CaseApi.createCase(payload);
    if (res?.success) {
      setModalOpen(false);
      setCreateCaseFormData(JSON.parse(JSON.stringify(AddNewCaseForm)));
      getChatListByUserId();
    }
  };

  const HandleFormDataChange = (
    data: HandleChangeProps,
    arr: FormDataType[]
  ) => {
    setCreateCaseFormData(HandleFormChange(data, arr));
  };

  return (
    <>
      <ModalLayout
        content={
          <CustomModal.CreateCaseModal
            formArr={createCaseFormData}
            handleChange={HandleFormDataChange}
          />
        }
        open={modalOpen}
        modwidth="400px"
        onClose={() => setModalOpen(false)}
        heading="New Case"
        buttonArr={[
          {
            id: "createCase",
            label: "Create Case",
            clickHandler: handleCreateCase,
          },
          {
            id: "cancel",
            label: "Cancel",
            clickHandler: () => setModalOpen(false),
            type: "border",
          },
        ]}
      />
      <Box
        sx={{
          width: { xs: "100%", sm: 350 },
          height: "100%",
          // Version 4: Lightweight White Glass
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          borderRight: "1px solid rgba(0, 0, 0, 0.08)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {/* Header and Search */}
        <Box
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2.5}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: "#fff",
                letterSpacing: "-0.5px",
              }}
            >
              Messages
            </Typography>
            {isLegalCounselChat && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCreateCaseFormData(
                    JSON.parse(JSON.stringify(AddNewCaseForm))
                  );
                  setModalOpen(true);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(255, 255, 255, 0.1)",
                  fontWeight: 600,
                  bgcolor: "#fff",
                  color: "#1a237e",
                  "&:hover": {
                    bgcolor: alpha("#fff", 0.9)
                  }
                }}
              >
                New Case
              </Button>
            )}
          </Stack>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              px: 2,
              py: 1.25,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              "&:focus-within": {
                backgroundColor: "rgba(255, 255, 255, 1)",
                boxShadow: "0 4px 25px rgba(0, 0, 0, 0.2)",
                borderColor: "#fff",
                "& .MuiSvgIcon-root": { color: "#1a237e" },
                "& input": { color: "#1a237e" }
              },
            }}
          >
            <SearchIcon sx={{ color: "rgba(255, 255, 255, 0.5)", mr: 1, fontSize: 20 }} />
            <InputBase
              placeholder="Search conversations..."
              fullWidth
              sx={{ fontSize: "14px", fontWeight: 500, color: "#fff" }}
              inputProps={{ "aria-label": "search" }}
              onChange={(e) => setInputVal(e.target.value)}
            />
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Loader />
          </Box>
        ) : (
          <List
            disablePadding
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0, // Important for flex child to be scrollable
            }}
          >
            {caseList.map((chat: CaseListType, index) => (
              <React.Fragment key={chat.caseId}>
                <ListItem
                  sx={{
                    mx: 1,
                    mb: 1,
                    width: "calc(100% - 16px)",
                    borderRadius: "16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: selectedCase?.caseId == chat.caseId
                      ? alpha("#1a237e", 0.06)
                      : "transparent",
                    "&:hover": {
                      backgroundColor: alpha("#1a237e", 0.04),
                      transform: "translateY(-1px)",
                    },
                    alignItems: "center",
                    py: 2,
                    px: 2,
                    ...(selectedCase?.caseId == chat.caseId && {
                      boxShadow: "0 4px 12px rgba(26, 35, 126, 0.1)",
                      borderLeft: "4px solid #1a237e"
                    })
                  }}
                  onClick={() => {
                    if (isMobile) setShowChatList(false);
                    setSelectedCase(chat);
                    getSelectedCase(chat);
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      alt={chat.caseType}
                      sx={{
                        width: 48,
                        height: 48,
                        background: selectedCase?.caseId == chat.caseId
                          ? "linear-gradient(135deg, #1a237e, #3949ab)"
                          : "#f0f2f5",
                        color: selectedCase?.caseId == chat.caseId ? "#fff" : "#1a237e",
                        fontSize: "16px",
                        fontWeight: "bold",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      }}
                    >
                      {getInitials(chat.orderType)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="baseline"
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "15px",
                            fontWeight: 700,
                            color: "#1a237e",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "180px",
                          }}
                        >
                          {isLegalCounselChat
                            ? `${chat?.description} - ${chat.caseId}`
                            : (myInfo.userType === "ADMIN" || myInfo.userType === "SUPERADMIN")
                              ? (chat.orderType === "Admin Support" || chat.orderTypeId === 53)
                                ? (chat.clientUserType === "CORPORATEUSER"
                                  ? `${chat.clientFullName} (${chat.clientOrganizationName || "N/A"}) `
                                  : `${chat.clientFullName} - (${formatUserType(chat.clientUserType)}) `)
                                : `${chat.clientFullName} - ${chat.orderType} - ${chat.caseNumber ? chat.caseNumber : chat.caseId}`
                              : `${chat.orderType} - ${chat.caseNumber ? chat.caseNumber : chat.caseId} - ${chat.caseType}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.primary", ml: 1, flexShrink: 0, opacity: 0.7 }}>
                          {DateTime.ToLocalDate(chat.statusLastUpdated)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.primary",
                          display: "block",
                          mt: 0.5,
                          fontSize: "12px",
                          opacity: 0.6
                        }}
                      >
                        {chat.caseNumber ? `Case ID: ${chat.caseNumber}` : `Case ID: ${chat.caseId}`}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </>
  );
};
export default ChatListComponent;

const sxSelected = {
  backgroundColor: "#f3ecebff",
};
